/**
 * Directory Indexer with Differential Sync, Resumable Indexing, and Auto-Tagging
 * Recursively scans a FileSystemDirectoryHandle, hashes files, extracts tags & BPM,
 * compares against IndexedDB to add new, reindex updated, and remove deleted files.
 */

import type { Track, Tag, IndexingStatus } from './types.ts';
import { dbManager } from './db.ts';
import { BPM_ANALYSIS_VERSION, calculateSHA1, extractTrackMetadata, getDeterministicGradient } from './metadata.ts';

const AUDIO_EXTENSIONS = new Set(['.ogg', '.oga', '.mp3', '.wav', '.m4a', '.flac', '.aac', '.weba', '.opus']);

interface DiscoveredFile {
    path: string;
    filename: string;
    ext: string;
    handle: FileSystemFileHandle;
    size: number;
    lastModified: number;
}

export class DirectoryIndexer {
    private isAborted: boolean = false;

    public abort(): void {
        this.isAborted = true;
    }

    /**
     * Recursively traverses a directory handle collecting all audio files.
     */
    public async collectAudioFiles(
        dirHandle: FileSystemDirectoryHandle,
        currentPath: string = ''
    ): Promise<DiscoveredFile[]> {
        const results: DiscoveredFile[] = [];

        // @ts-ignore - async iterator on directory handle
        for await (const entry of dirHandle.values()) {
            if (this.isAborted) break;

            const entryPath = currentPath ? `${currentPath}/${entry.name}` : entry.name;

            if (entry.kind === 'file') {
                const ext = '.' + entry.name.split('.').pop()?.toLowerCase();
                if (AUDIO_EXTENSIONS.has(ext)) {
                    try {
                        const fileHandle = entry as FileSystemFileHandle;
                        const file = await fileHandle.getFile();
                        results.push({
                            path: entryPath,
                            filename: entry.name,
                            ext: ext,
                            handle: fileHandle,
                            size: file.size,
                            lastModified: file.lastModified
                        });
                    } catch (err) {
                        console.warn(`Could not read file ${entryPath}:`, err);
                    }
                }
            } else if (entry.kind === 'directory') {
                const subDirHandle = entry as FileSystemDirectoryHandle;
                const subResults = await this.collectAudioFiles(subDirHandle, entryPath);
                results.push(...subResults);
            }
        }

        return results;
    }

    /**
     * Maps track filename and metadata to default tags.
     */
    public deriveDefaultTags(filename: string, path: string, tags: Tag[]): string[] {
        const assignedTagIds: string[] = [];
        const lower = `${filename} ${path}`.toLowerCase();

        for (const tag of tags) {
            const tName = tag.name.toLowerCase();
            if (tName.includes('club') && (lower.includes('club') || lower.includes('electro'))) {
                assignedTagIds.push(tag.id);
            } else if (tName.includes('snap') && (lower.includes('pulse') || lower.includes('snap') || lower.includes('house'))) {
                assignedTagIds.push(tag.id);
            } else if (tName.includes('slow') && (lower.includes('slow') || lower.includes('sweetly') || lower.includes('chill'))) {
                assignedTagIds.push(tag.id);
            } else if (tName.includes('funk') && (lower.includes('funk') || lower.includes('springs') || lower.includes('lean') || lower.includes('hip-hop'))) {
                assignedTagIds.push(tag.id);
            } else if (tName.includes('remastered') && lower.includes('remastered')) {
                assignedTagIds.push(tag.id);
            }
        }

        return assignedTagIds;
    }

    /**
     * Performs full or differential sync on the given directory handle.
     */
    public async syncDirectory(
        dirHandle: FileSystemDirectoryHandle,
        onProgress: (status: IndexingStatus) => void
    ): Promise<Track[]> {
        this.isAborted = false;

        // 1. Collect all audio files in the directory
        const discovered = await this.collectAudioFiles(dirHandle);
        const existingTracks = await dbManager.getAllTracks();
        const existingMapByPath = new Map<string, Track>(existingTracks.map(t => [t.path, t]));
        const tags = await dbManager.getAllTags();

        const discoveredPaths = new Set(discovered.map(d => d.path));

        // 2. Remove tracks that were deleted from the directory
        for (const [path, track] of existingMapByPath.entries()) {
            if (!discoveredPaths.has(path)) {
                await dbManager.deleteTrack(track.id);
            }
        }

        // 3. Determine which files need indexing vs which can be reused
        const filesToIndex: DiscoveredFile[] = [];
        const finalTracks: Track[] = [];

        for (const fileItem of discovered) {
            const existing = existingMapByPath.get(fileItem.path);

            // If file size and last modified date match, reuse existing indexed metadata!
            if (existing && existing.size === fileItem.size && existing.lastModified === fileItem.lastModified) {
                // Update file handle reference in case handle instance changed
                existing.handle = fileItem.handle;
                if (existing.bpmSource !== 'manual' && existing.bpmAnalysisVersion !== BPM_ANALYSIS_VERSION) {
                    const metadata = await extractTrackMetadata(await fileItem.handle.getFile(), fileItem.path);
                    existing.bpm = metadata.bpm || null;
                    existing.bpmSource = metadata.bpmSource;
                    existing.bpmAnalysisVersion = BPM_ANALYSIS_VERSION;
                }
                await dbManager.saveTrack(existing);
                finalTracks.push(existing);
            } else {
                filesToIndex.push(fileItem);
            }
        }

        const totalToProcess = filesToIndex.length;
        let processedCount = 0;

        const status: IndexingStatus = {
            isIndexing: totalToProcess > 0,
            lastIndexedAt: Date.now(),
            totalFiles: discovered.length,
            processedFiles: discovered.length - totalToProcess,
            currentFile: null,
            pendingPaths: filesToIndex.map(f => f.path),
            directoryName: dirHandle.name
        };

        await dbManager.saveIndexingStatus(status);
        onProgress(status);

        // 4. Process new or updated files
        for (const item of filesToIndex) {
            if (this.isAborted) break;

            status.currentFile = item.filename;
            status.processedFiles++;
            status.pendingPaths = status.pendingPaths.filter(p => p !== item.path);
            await dbManager.saveIndexingStatus(status);
            onProgress(status);

            try {
                const file = await item.handle.getFile();
                const sha1 = await calculateSHA1(file);
                const metadata = await extractTrackMetadata(file, item.path);

                // Preserve existing favorite or tags if updating an edited file
                const existing = existingMapByPath.get(item.path);
                const assignedTags = existing && existing.tags.length > 0
                    ? existing.tags
                    : this.deriveDefaultTags(item.filename, item.path, tags);

                const track: Track = {
                    id: sha1,
                    path: item.path,
                    filename: item.filename,
                    title: metadata.title || item.filename,
                    artist: metadata.artist || 'Dance Sessions',
                    album: metadata.album || 'Dance Vault',
                    durationMs: metadata.durationMs || 0,
                    bpm: metadata.bpm || null,
                    bpmSource: metadata.bpmSource,
                    bpmAnalysisVersion: BPM_ANALYSIS_VERSION,
                    tags: assignedTags,
                    size: item.size,
                    lastModified: item.lastModified,
                    ext: item.ext,
                    handle: item.handle,
                    gradient: getDeterministicGradient(item.path),
                    favorite: existing ? existing.favorite : false
                };

                await dbManager.saveTrack(track);
                finalTracks.push(track);
            } catch (err) {
                console.error(`Error indexing file ${item.path}:`, err);
            }

            processedCount++;
        }

        // 5. Indexing finished
        status.isIndexing = false;
        status.currentFile = null;
        status.lastIndexedAt = Date.now();
        status.pendingPaths = [];
        await dbManager.saveIndexingStatus(status);
        onProgress(status);

        return dbManager.getAllTracks();
    }
}

export const directoryIndexer = new DirectoryIndexer();
